/**
 * The console's paged lists (pólizas, cotizaciones, prospectos, órdenes, clientes): the API
 * answers `{ items, nextCursor }` when asked with `limit`, and «Cargar más» asks for one page
 * more. A re-render (after an action, or a refresh) loads the same number of pages again, so
 * what the person already opened stays on screen with fresh data.
 */
export type Page<T> = { items: T[]; nextCursor: string | null };

/** How many items a page brings (the API allows at most 100). */
export const PAGE_SIZE = 50;

/** The URL of one page of a list: its own query (filters) plus limit and cursor. */
export function pageUrl(path: string, limit: number, cursor: string | null): string {
  const q = new URLSearchParams({ limit: String(limit) });
  if (cursor) q.set('cursor', cursor);
  return `${path}${path.includes('?') ? '&' : '?'}${q}`;
}

/**
 * Loads up to `pages` pages in order, following the cursors; `more` says whether the list goes
 * on after them (the «Cargar más» button).
 */
export async function loadPages<T>(
  fetchPage: (cursor: string | null) => Promise<Page<T>>,
  pages: number,
): Promise<{ items: T[]; more: boolean }> {
  const items: T[] = [];
  let cursor: string | null = null;
  for (let i = 0; i < Math.max(1, pages); i++) {
    const page: Page<T> = await fetchPage(cursor);
    items.push(...page.items);
    cursor = page.nextCursor;
    if (!cursor) break;
  }
  return { items, more: cursor !== null };
}
